/**
 * Estimates how long a shared design takes to actually sew, from the stats the
 * creator's export left behind (see stitchStats on the public metadata document).
 *
 * Two things make up the wait. The machine runs at a fairly predictable rate, so
 * stitch count gives the bulk of it; the rest is the person, stopping to rethread
 * at every color change. A one-color design is close to pure machine time, while
 * an eight-color design spends several minutes with the machine idle.
 *
 * SPM is deliberately conservative. Home machines top out anywhere from 400 to
 * 1000, and nobody runs flat out through a whole design — there are slowdowns on
 * dense fills and tight curves. Quoting the headline speed would produce a number
 * every user beats by a mile in the wrong direction.
 *
 * Necessarily approximate, and for the same reason the difficulty badge is: these
 * come from the creator's export at their hoop size, and whoever opens the design
 * re-exports at their own, which moves the stitch count.
 */

const STITCHES_PER_MINUTE = 650;
const MINUTES_PER_COLOR_CHANGE = 0.75;

const formatMinutes = (minutes) => {
  if (minutes < 1) return "< 1 min";
  if (minutes < 60) return `~${Math.round(minutes)} min`;

  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes - hours * 60);
  // 90 rounds to "1 h 30 min", but 119.6 must not round to "1 h 60 min".
  if (rest === 60) return `~${hours + 1} h`;
  return rest ? `~${hours} h ${rest} min` : `~${hours} h`;
};

/**
 * @param {{num_stitches?: number|null, num_colors?: number|null}} stitchStats
 * @returns {{label: string, detail: string}|null} null when the design was
 *   published without export stats, in which case the caller should show nothing
 *   rather than guess.
 */
export const getStitchTime = (stitchStats) => {
  const stitches = Number(stitchStats?.num_stitches) || 0;
  if (!stitches) return null;

  const colors = Number(stitchStats?.num_colors) || 0;
  const runMinutes = stitches / STITCHES_PER_MINUTE;
  // The first color is already threaded, so only the changes after it cost time.
  const changeMinutes = Math.max(0, colors - 1) * MINUTES_PER_COLOR_CHANGE;

  const detail =
    changeMinutes > 0
      ? `About ${formatMinutes(runMinutes).replace("~", "")} of machine time at ${STITCHES_PER_MINUTE} stitches per minute, plus ${colors - 1} thread ${colors - 1 === 1 ? "change" : "changes"}. Your own size and machine will shift this.`
      : `About ${formatMinutes(runMinutes).replace("~", "")} at ${STITCHES_PER_MINUTE} stitches per minute, in a single color. Your own size and machine will shift this.`;

  return { label: formatMinutes(runMinutes + changeMinutes), detail };
};
